/** Inputs — design spec §7. Text, multiline, select, date, error state. */

import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * DS `Field`: `surface`, a `border-strong` outline (3:1, so the field is seen),
 * radius 8, height ≥46, a `focus` ring. 16px text: iOS does not zoom into it.
 * An invalid field is `blocked` on its tint — field errors are the one other
 * place that colour appears (Spec 020 D2).
 */
const FIELD_FRAME =
  "rounded-compact border border-p-border-strong bg-p-surface outline-none " +
  "focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-p-focus";
const FIELD_BASE =
  `w-full ${FIELD_FRAME} px-3 py-[11px] text-[16px] leading-[22px] text-p-text placeholder:text-p-text-muted ` +
  "focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-p-focus";
const FIELD_INVALID = "border-p-blocked bg-p-blocked-tint";

function FieldShell({
  label,
  error,
  hint,
  htmlFor,
  children,
}: {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label != null && (
        // ui-ux-pro-max: every input gets a real <label for=…>.
        <label htmlFor={htmlFor} className="text-body text-p-text-muted">
          {label}
        </label>
      )}
      {children}
      {/* DS: the error is 13/18 `blocked` directly under the field. */}
      {error ? (
        <p className="text-meta text-p-blocked">{error}</p>
      ) : hint != null ? (
        <p className="text-meta text-p-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
}

export function TextField({ label, error, hint, className, id, ...rest }: TextFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} error={error} hint={hint} htmlFor={fieldId}>
      <input
        id={fieldId}
        className={cn(FIELD_BASE, "min-h-[46px]", error && FIELD_INVALID, className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </FieldShell>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
}

/** §7 — multiline starts at 88px. */
export function TextArea({ label, error, hint, className, id, ...rest }: TextAreaProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} error={error} hint={hint} htmlFor={fieldId}>
      <textarea
        id={fieldId}
        className={cn(
          FIELD_BASE,
          "min-h-textarea resize-none",
          error && FIELD_INVALID,
          className,
        )}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}

/**
 * §7 — a select is a cell showing the current value with a `chevron-down`. We
 * keep a real <select> underneath so the platform picker, keyboard and screen
 * readers all work; it is transparent and stretched over the cell.
 */
export function SelectField({
  label,
  error,
  hint,
  value,
  onChange,
  options,
  placeholder = "Не выбрано",
  disabled,
}: SelectFieldProps) {
  const fieldId = useId();
  const selected = options.find((o) => o.value === value);
  return (
    <FieldShell label={label} error={error} hint={hint} htmlFor={fieldId}>
      <div
        className={cn(
          "relative flex min-h-[46px] items-center gap-2 px-3 py-[11px]",
          FIELD_FRAME,
          error && FIELD_INVALID,
          disabled && "opacity-40",
        )}
      >
        <span className={cn("truncate-line flex-1 text-[16px] leading-[22px]", selected ? "text-p-text" : "text-p-text-muted")}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className="h-[18px] w-[18px] shrink-0 text-p-text-muted" aria-hidden="true" />
        <select
          id={fieldId}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full opacity-0"
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </FieldShell>
  );
}

export interface DateFieldProps {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  /** ISO `yyyy-mm-dd`, or empty. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/** `calendar-days` icon; the value in the text colour — it is a value, not a link. */
export function DateField({
  label,
  error,
  hint,
  value,
  onChange,
  placeholder = "Без срока",
  disabled,
}: DateFieldProps) {
  const fieldId = useId();
  return (
    <FieldShell label={label} error={error} hint={hint} htmlFor={fieldId}>
      <div
        className={cn(
          "relative flex min-h-[46px] items-center gap-2 px-3 py-[11px]",
          FIELD_FRAME,
          error && FIELD_INVALID,
          disabled && "opacity-40",
        )}
      >
        <CalendarDays className="h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
        <span className={cn("flex-1 text-[16px] leading-[22px]", value ? "text-p-text" : "text-p-text-muted")}>
          {value ? new Date(value).toLocaleDateString("ru-RU") : placeholder}
        </span>
        <input
          id={fieldId}
          type="date"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full opacity-0"
        />
      </div>
    </FieldShell>
  );
}

/** §8 — the 36px search chip on the Tools screen. */
export function SearchField({
  value,
  onChange,
  placeholder = "Поиск",
  icon,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: ReactNode;
}) {
  return (
    <div className={cn("flex h-compact items-center gap-2 px-2.5", FIELD_FRAME, "border-p-border")}>
      {icon}
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent text-[16px] text-p-text outline-none placeholder:text-p-text-muted"
      />
    </div>
  );
}
